import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  Paper,
  Tab,
  Tabs,
  Toolbar,
  Typography
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import {
  createAccountEntityPermission,
  createUserEntityPermission,
  deleteAccountEntityPermission,
  deleteUserEntityPermission,
  getAccountEntityPermissions,
  getUserEntityPermissions
} from "../../../api/entityPermissions";
import AccountEntityPermissionsTable from "../../../components/EntityPermissions/AccountEntityPermissionsTable";
import { useErrorHandler } from "../../../hooks";
import { setTab } from "../../../store/UI";
import { AccountEntityPermission } from "../../../types/model/EntityPermission/AccountEntityPermission";
import { UserEntityPermission } from "../../../types/model/EntityPermission/UserEntityPermission";
import { UIStore } from "../../../types/store";
import UserEntityPermissionsTable from "../../../components/EntityPermissions/UserEntityPermissionsTable";
import { useAccountPermissions } from "../../../hooks/Permissions/useAccountPermissions";

export default function EntityPermissions() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const location = useLocation();
  const { canEditData } = useAccountPermissions();
 
  const tabState = useSelector((state: { ui: UIStore }) => state.ui.tabState);
  const [tabIndex, setTabIndex] = useState(tabState[location.pathname] || 0);
 
  const [accountPermissions, setAccountPermissions] = useState<AccountEntityPermission[]>([]);
  const [isLoadingAccountPermissions, setIsLoadingAccountPermissions] = useState(false);
  const [accountPermissionToDelete, setAccountPermissionToDelete] =  useState<AccountEntityPermission | null>(null);
  const [isDeletingAccountPermission, setIsDeletingAccountPermission] = useState(false);
  const [accountFilters, setAccountFilters] = useState<{
    accountId?: number;
    entityId?: number;
  }>({});
 
  const [userPermissions, setUserPermissions] = useState<UserEntityPermission[]>([]);
  const [isLoadingUserPermissions, setIsLoadingUserPermissions] = useState(false);
  const [userPermissionToDelete, setUserPermissionToDelete] = useState<UserEntityPermission | null>(null);
  const [isDeletingUserPermission, setIsDeletingUserPermission] = useState(false);
  const [userFilters, setUserFilters] = useState<{
    userId?: number;
    entityId?: number;
  }>({});

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
    dispatch(setTab({ page: location.pathname, tab: newValue }));
  };

  // Account permissions handlers
  const refreshAccountPermissions = async () => {
    setIsLoadingAccountPermissions(true);
    try {
      const response = await getAccountEntityPermissions(
        accountFilters.accountId,
        accountFilters.entityId
      );

      setAccountPermissions(response);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoadingAccountPermissions(false);
    }
  };

  const handleCreateAccountPermission = async (values: {
    entityId: number;
    accountId: number;
    permissionType: "read" | "write";
  }) => {
    await createAccountEntityPermission(values.accountId, values.entityId, values.permissionType);
    await refreshAccountPermissions();
  };

  const handleAccountDeleteClick = (permission: AccountEntityPermission) => {
    setAccountPermissionToDelete(permission);
  };
 
  const handleCancelAccountDelete = () => {
    setAccountPermissionToDelete(null);
  };
 
  const handleConfirmAccountDelete = async () => {
    if (accountPermissionToDelete === null) return;
 
    setIsDeletingAccountPermission(true);
    try {
      await deleteAccountEntityPermission(
        accountPermissionToDelete.accountId,
        accountPermissionToDelete.entityId
      );

      await refreshAccountPermissions();
    } catch (error) {
      handleError(error);
    } finally {
      setIsDeletingAccountPermission(false);
      setAccountPermissionToDelete(null);
    }
  };

  // User permissions handlers
  const refreshUserPermissions = async () => {
    setIsLoadingUserPermissions(true);

    try {
      const response = await getUserEntityPermissions(
        userFilters.userId,
        userFilters.entityId
      );
      
      setUserPermissions(response);
    } catch (error) {
      handleError(error);
    } finally {
      setIsLoadingUserPermissions(false);
    }
  };

  const handleCreateUserPermission = async (values: {
    entityId: number;
    accountId: number;
    userId: number;
    permissionType: "read" | "write";
  }) => {
    await createUserEntityPermission(values.accountId, values.userId, values.entityId, values.permissionType);
    await refreshUserPermissions();
  };

  const handleUserDeleteClick = (permission: UserEntityPermission) => {
    setUserPermissionToDelete(permission);
  };
 
  const handleCancelUserDelete = () => {
    setUserPermissionToDelete(null);
  };
 
  const handleConfirmUserDelete = async () => {
    if (userPermissionToDelete === null) return;

    setIsDeletingUserPermission(true);

    try {
      await deleteUserEntityPermission(
        userPermissionToDelete.accountId,
        userPermissionToDelete.userId,
        userPermissionToDelete.entityId
      );

      await refreshUserPermissions();
    } catch (error) {
      handleError(error);
    } finally {
      setIsDeletingUserPermission(false);
      setUserPermissionToDelete(null);
    }
  };

  useEffect(() => {
    refreshAccountPermissions();
  }, [accountFilters]);
 
  useEffect(() => {
    if (tabIndex === 1) {
      refreshUserPermissions();
    }
  }, [tabIndex, userFilters]);
 
  return (
    <Paper elevation={3} sx={{ borderRadius: 2 }}>
      <Toolbar>
        <Typography sx={{ flex: "1 1 100%" }} variant="h4">
          {t("admin.entity-permissions.title")}
        </Typography>
      </Toolbar>
 
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        <Tab label={t("admin.entity-permissions.tabs.accounts")} />
        <Tab label={t("admin.entity-permissions.tabs.users")} />
      </Tabs>
 
      <Box sx={{ p: 3 }}>
        <Fade in={tabIndex === 0} timeout={300} unmountOnExit>
          <Box>
            {tabIndex === 0 && (
              <AccountEntityPermissionsTable
                accountEntityPermissionsData={accountPermissions}
                canEdit={canEditData}
                isLoading={isLoadingAccountPermissions}
                onCreate={handleCreateAccountPermission}
                onDelete={handleAccountDeleteClick}
                onRefresh={refreshAccountPermissions}
                onApplyFilters={setAccountFilters}
              />
            )}
          </Box>
        </Fade>
 
        <Fade in={tabIndex === 1} timeout={300} unmountOnExit>
          <Box>
            {tabIndex === 1 && (
              <UserEntityPermissionsTable
                userEntityPermissionsData={userPermissions}
                canEdit={canEditData}
                isLoading={isLoadingUserPermissions}
                onCreate={handleCreateUserPermission}
                onDelete={handleUserDeleteClick}
                onRefresh={refreshUserPermissions}
                onApplyFilters={setUserFilters}
              />
            )}
          </Box>
        </Fade>
      </Box>
 
      <Dialog open={accountPermissionToDelete !== null} onClose={handleCancelAccountDelete}>
        <DialogTitle>{t("admin.entity-permissions.delete-confirmation-title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("admin.entity-permissions.delete-confirmation-message")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAccountDelete} disabled={isDeletingAccountPermission}>
            {t("generic.cancel")}
          </Button>
          <Button
            onClick={handleConfirmAccountDelete}
            color="error"
            variant="contained"
            disabled={isDeletingAccountPermission}
            startIcon={isDeletingAccountPermission ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t("generic.delete")}
          </Button>
        </DialogActions>
      </Dialog>
 
      <Dialog open={userPermissionToDelete !== null} onClose={handleCancelUserDelete}>
        <DialogTitle>{t("admin.entity-permissions.delete-confirmation-title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("admin.entity-permissions.delete-confirmation-message")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUserDelete} disabled={isDeletingUserPermission}>
            {t("generic.cancel")}
          </Button>
          <Button
            onClick={handleConfirmUserDelete}
            color="error"
            variant="contained"
            disabled={isDeletingUserPermission}
            startIcon={isDeletingUserPermission ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {t("generic.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
